'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Thumbnail } from '@/app/saved/components/Thumbnail';
import { TYPE_META, badgeLabel, openHref, openHrefAtVersion, isOlderVersion, folderNameProblem, tagProblem, TAG_MAX, type LibraryItem } from '@/app/saved/lib/library';
import { useVersion } from '@/app/context/VersionContext';
import { isLootVersionSupported } from '@/app/tools/loot-table-builder/lib/constants';
import PublishDesignPanel from './PublishDesignPanel';
import { MODAL_BACKDROP, MODAL_PANEL } from '@/app/components/ui/motion';

const NEW_FOLDER = '__new__';

export interface QuickLookModalProps {
  item: LibraryItem;
  folders: string[];
  allTags: string[];
  copied: boolean;
  onClose: () => void;
  onRename: (item: LibraryItem, name: string) => void;
  onSetFolder: (elementIds: number[], folder: string | null) => void;
  onSetTags: (item: LibraryItem, tags: string[]) => void;
  onSetPinned: (item: LibraryItem, pinned: boolean) => void;
  onDuplicate: (item: LibraryItem) => void;
  onDelete: (item: LibraryItem) => void;
  onCopyShare: (item: LibraryItem) => void;
  onOpenTool: (item: LibraryItem) => void;
  onToast: (message: string) => void;
}

export function QuickLookModal(props: QuickLookModalProps) {
  const { item, folders, allTags, copied, onClose, onRename, onSetFolder, onSetTags, onSetPinned, onDuplicate, onDelete, onCopyShare, onOpenTool, onToast } = props;
  const def = TYPE_META[item.type];
  const editable = item.source === 'element';
  // Element builds only — favorites open version-less via openHref, so no dual "open at version".
  const { version: working } = useVersion();
  const old = item.source === 'element' && isOlderVersion(item.version, working);
  // The loot builder cannot write the working version's format (26.3+ rewrote it), so "Open at
  // {working}" would land on its version gate. Offer only the version the table was made for.
  const canOpenAtWorking = !(item.type === 'loot_table' && !isLootVersionSupported(working));

  // The parent keys this component on item.uid, so opening a different card remounts it and the
  // name draft re-initialises from item.name — no sync effect needed.
  const [name, setName] = useState(item.name);
  const [tagInput, setTagInput] = useState('');
  const [suggestOpen, setSuggestOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const commitName = () => {
    const trimmed = name.trim();
    if (trimmed && trimmed !== item.name) onRename(item, trimmed);
    else setName(item.name);
  };

  const addTag = (t: string) => {
    const tag = t.trim().toLowerCase().replace(/\s+/g, '-');
    if (!tag || item.tags.includes(tag)) { setTagInput(''); return; }
    const problem = tagProblem(tag);
    if (problem) { onToast(problem); return; }
    onSetTags(item, [...item.tags, tag]);
    setTagInput('');
    setSuggestOpen(false);
  };
  const removeTag = (t: string) => onSetTags(item, item.tags.filter(x => x !== t));

  const changeFolder = (value: string) => {
    if (value === NEW_FOLDER) {
      const f = window.prompt('New folder name')?.trim();
      if (!f) return;
      // Give the same feedback the page's drag/bulk paths do, instead of silently doing nothing.
      const problem = folderNameProblem(f);
      if (problem) { onToast(problem); return; }
      onSetFolder([item.id], f);
      return;
    }
    onSetFolder([item.id], value === '' ? null : value);
  };

  const suggestions = allTags.filter(t => !item.tags.includes(t) && t.includes(tagInput.trim().toLowerCase())).slice(0, 6);

  return (
    <div className={`fixed inset-0 z-[60] flex items-center justify-center p-5 bg-zinc-950/70 backdrop-blur-sm ${MODAL_BACKDROP}`} onClick={onClose}>
      <div
        className={`w-full max-w-[720px] max-h-[88dvh] overflow-auto bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl relative ${MODAL_PANEL}`}
        role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-2.5 right-2.5 z-10 w-8 h-8 flex items-center justify-center rounded-lg bg-zinc-950/60 border border-zinc-800 text-zinc-400 hover:text-zinc-100" title="Close">✕</button>

        <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr]">
          <div className="min-h-[180px] sm:min-h-full relative">
            <Thumbnail item={item} size="modal" />
          </div>

          <div className="p-5 flex flex-col gap-3.5">
            {editable ? (
              <input
                value={name} onChange={e => setName(e.target.value)} onBlur={commitName}
                onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                aria-label="Name"
                className="text-lg font-bold text-zinc-100 bg-transparent border border-transparent hover:border-zinc-800 focus:border-violet-500/50 focus:bg-zinc-800/50 rounded-lg px-2 py-1 -mx-2 focus:outline-none"
              />
            ) : (
              <div className="text-lg font-bold text-zinc-100 px-0.5">{item.name}</div>
            )}

            <div className="flex items-center gap-2 text-[12px] text-zinc-500">
              <span className="flex items-center gap-1.5 px-1.5 py-0.5 rounded-full text-[10.5px] font-semibold bg-zinc-800 text-zinc-300">
                <span className="w-1.5 h-1.5 rounded-sm" style={{ background: def.hex }} />{badgeLabel(item.type)}
              </span>
              {item.version && <span className="font-mono">{item.version}</span>}
              <span className="flex-1" />
              {editable && (
                <button onClick={() => onSetPinned(item, !item.pinned)} title={item.pinned ? 'Unpin' : 'Pin to top'} className={item.pinned ? 'text-amber-400' : 'text-zinc-500 hover:text-zinc-300'}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M9 4h6l-1 7 4 3v2H6v-2l4-3z M12 16v4" /></svg>
                </button>
              )}
            </div>

            {item.summary && <p className="text-[12px] text-zinc-500 leading-relaxed line-clamp-3 -mt-1">{item.summary}</p>}

            {editable && (
              <>
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.08em] text-zinc-600 font-bold mb-1.5">Tags</div>
                  <div className="flex flex-wrap gap-1.5 items-center">
                    {item.tags.map(t => (
                      <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-800 border border-zinc-800 text-zinc-300 flex items-center gap-1">
                        {t}<button onClick={() => removeTag(t)} className="text-zinc-500 hover:text-red-400" aria-label={`Remove ${t}`}>×</button>
                      </span>
                    ))}
                    <div className="relative">
                      <input
                        value={tagInput}
                        maxLength={TAG_MAX}
                        onChange={e => { setTagInput(e.target.value); setSuggestOpen(true); }}
                        onFocus={() => setSuggestOpen(true)}
                        onBlur={() => setTimeout(() => setSuggestOpen(false), 120)}
                        onKeyDown={e => { if (e.key === 'Enter') addTag(tagInput); }}
                        placeholder="+ tag"
                        className="bg-zinc-800 border border-zinc-800 rounded-full px-2.5 py-0.5 text-[11px] text-zinc-200 w-24 focus:outline-none focus:border-emerald-500/50"
                      />
                      {suggestOpen && (suggestions.length > 0 || tagInput.trim()) && (
                        <div className="absolute top-full left-0 mt-1 z-10 min-w-[150px] bg-zinc-800 border border-zinc-700 rounded-lg overflow-hidden shadow-xl">
                          {suggestions.map(s => (
                            <button key={s} onMouseDown={e => { e.preventDefault(); addTag(s); }} className="block w-full text-left px-3 py-1.5 text-[12px] text-zinc-300 hover:bg-emerald-500/10 hover:text-emerald-400">{s}</button>
                          ))}
                          {tagInput.trim() && !allTags.includes(tagInput.trim().toLowerCase()) && (
                            <button onMouseDown={e => { e.preventDefault(); addTag(tagInput); }} className="block w-full text-left px-3 py-1.5 text-[12px] text-zinc-500 italic hover:bg-emerald-500/10 hover:text-emerald-400">Create “{tagInput.trim().toLowerCase()}”</button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.08em] text-zinc-600 font-bold mb-1.5">Folder</div>
                  <select
                    value={item.folder ?? ''} onChange={e => changeFolder(e.target.value)}
                    className="bg-zinc-800 border border-zinc-800 rounded-lg text-[12px] text-zinc-200 px-2.5 py-1.5 pr-8 cursor-pointer appearance-none focus:outline-none focus:border-violet-500/50"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%236b746c' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 9px center' }}
                  >
                    <option value="">Unfiled</option>
                    {folders.map(f => <option key={f} value={f}>{f}</option>)}
                    <option value={NEW_FOLDER}>+ New folder…</option>
                  </select>
                </div>
              </>
            )}

            {old && (
              <div className="flex items-start gap-2 text-[12px] text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 -mb-1">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4 shrink-0 mt-0.5"><circle cx="12" cy="12" r="9" /><path strokeLinecap="round" d="M12 7v5l3 2" /></svg>
                <span>
                  Made for <b>{item.version}</b> — you&apos;re working in <b>{working}</b>.{' '}
                  {canOpenAtWorking
                    ? 'Open it at either version below.'
                    : `Loot tables for ${working} aren't supported yet, so it opens at ${item.version}.`}
                </span>
              </div>
            )}

            <div className="flex gap-2 mt-1 pt-3.5 border-t border-zinc-800/70 flex-wrap">
              {old && canOpenAtWorking ? (
                <>
                  <Link href={openHrefAtVersion(item, working)} onClick={() => onOpenTool(item)} className={`flex-1 min-w-[130px] justify-center inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[12.5px] font-semibold ${def.tint}`}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path strokeLinecap="round" d="M13 7l5 5-5 5M6 12h12" /></svg>Open at {working}
                  </Link>
                  <Link href={openHrefAtVersion(item, item.version!)} onClick={() => onOpenTool(item)} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[12.5px] border border-zinc-800 bg-zinc-800/60 text-zinc-300 hover:text-zinc-100">
                    Open at {item.version}
                  </Link>
                </>
              ) : old ? (
                <Link href={openHrefAtVersion(item, item.version!)} onClick={() => onOpenTool(item)} className={`flex-1 min-w-[140px] justify-center inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[12.5px] font-semibold ${def.tint}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path strokeLinecap="round" d="M13 7l5 5-5 5M6 12h12" /></svg>Open at {item.version}
                </Link>
              ) : (
                <Link href={openHref(item)} onClick={() => onOpenTool(item)} className={`flex-1 min-w-[140px] justify-center inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[12.5px] font-semibold ${def.tint}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path strokeLinecap="round" d="M13 7l5 5-5 5M6 12h12" /></svg>{item.buttonLabel}
                </Link>
              )}
              <button onClick={() => onCopyShare(item)} className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[12.5px] border border-zinc-800 bg-zinc-800/60 ${copied ? 'text-emerald-400' : 'text-zinc-300 hover:text-violet-400'}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4M18 8a3 3 0 10-3-3 3 3 0 003 3zM6 15a3 3 0 103-3 3 3 0 00-3 3zM18 22a3 3 0 10-3-3 3 3 0 003 3z" /></svg>{copied ? 'Copied' : 'Share'}
              </button>
              {editable && (
                <button onClick={() => onDuplicate(item)} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[12.5px] border border-zinc-800 bg-zinc-800/60 text-zinc-300 hover:text-zinc-100" title="Duplicate">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15V5a2 2 0 012-2h10" /></svg>Duplicate
                </button>
              )}
              <button onClick={() => { onDelete(item); onClose(); }} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[12.5px] border border-zinc-800 bg-zinc-800/60 text-zinc-400 hover:text-red-400 hover:border-red-500/40" title="Delete">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path strokeLinecap="round" d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m-9 0l1 12a2 2 0 002 2h6a2 2 0 002-2l1-12" /></svg>
              </button>
              {/* Only a type with a community adapter can be published (DESIGN_TYPES in
                  app/lib/community/shared.ts); the panel renders nothing for any other type. */}
              {editable && <PublishDesignPanel item={item} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
